import React, {
  memo, useCallback, useEffect, useRef, useState,
} from 'react';

import { useDispatch, useSelector } from 'react-redux';
import { Grid, LinearProgress } from '@mui/material';

import { ObjectType } from '../../../../../types/objectType';
import { LIST_TYPES } from '../../../../../config';

import {
  getNextListPage,
  toggleIsFilterUpdated,
  toggleIsLoading,
} from '../../../../../redux/modules/civilWorkWorkerList';

import CivilWorksWorkerCards from './InfiniteScrollListsCards/CivilWorksWorkerCards/CivilWorksWorkerCards';
import { DetailedInfoModal } from './DetailedInfoModal/DetailedInfoModal';
import { InfiniteScrollListFilter } from './InfiniteScrollListFilter/InfiniteScrollListFilter';

import infiniteScrollListStyle from './infiniteScrollListStyle';
import { sendShowDetailsEventAnalytics, sendStatusChangeEventAnalytics } from '../../../../../helpers/analyticsHelpers';
import { StatusChangeObject } from '../../../../../types/statusChangeObject';
import worksWorkersAPI from '../../../../../services/worksWorkersAPI';
import { LoadingComponent } from '../../../../Common/LoadingComponent/LoadingComponent';

const InfiniteScrollList: React.FC<ObjectType> = ({ listType }) => {
  const classes = infiniteScrollListStyle();
  const lists = useSelector((store: ObjectType) => store.civilWorksPersonLists[listType]);
  const [open, setOpen] = useState(false);
  const [modalDetailedInfo, setModalDetailedInfo] = useState({});
  const [detailLoading, setDetailLoading] = useState(false);
  const [statusChangeLoading, setStatusChangeLoading] = useState(false);
  const detailRequestId = useRef(0);

  const { isFilterUpdated } = lists;

  const dispatch = useDispatch();

  const {
    isLoading,
    isError,
    moreResults,
    totalResults,
    results,
  } = lists;

  const observer = useRef<IntersectionObserver>();
  const scrollToTop = useRef<null | HTMLDivElement>(null);

  const scrollToRef = () => {
    if (scrollToTop.current?.offsetTop) {
      scrollToTop.current?.scrollIntoView({ block: 'end' });
    }
  };

  const lastItemList = useCallback(
    (node) => {
      if (isLoading || isFilterUpdated) return;
      if (observer.current) observer.current.disconnect();
      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && moreResults) {
          dispatch(toggleIsLoading(listType));
          dispatch(getNextListPage(listType));
        }
      });
      if (node) observer.current.observe(node);
    },
    [isLoading, moreResults, isFilterUpdated],
  );

  const handleOpen = async (id: string, eventCategory: string) => {
    if (listType === LIST_TYPES.civilWorks) {
      try {
        const response = await worksWorkersAPI.getCivilWorkDetail(id);
        const detailedInfo = response.data?.result;
        if (!detailedInfo) return;

        setModalDetailedInfo(detailedInfo);
        setOpen(true);
        sendShowDetailsEventAnalytics(eventCategory, id);
      } catch {
        return;
      }
      return;
    }

    const employee = results.find((item: ObjectType) => item.id === id);
    if (!employee) return;
    let specialities = '';
    employee.experiencesEmployee?.forEach((speciality: any) => {
      specialities += `${speciality.experiencia} (${speciality.nivel_experiencia}), `;
    });

    const summaryInfo = {
      ...employee,
      especialidades: specialities.replace(/, $/, ''),
      experiencias: [],
    };

    setModalDetailedInfo(summaryInfo);
    setOpen(true);
    sendShowDetailsEventAnalytics(eventCategory, id);

    if (listType !== LIST_TYPES.workers) return;

    setDetailLoading(true);

    const requestId = detailRequestId.current + 1;
    detailRequestId.current = requestId;

    try {
      // eslint-disable-next-line max-len
      const professionalExperience = await worksWorkersAPI.getProfessionalExperienceByEmployeeId(id);

      if (requestId === detailRequestId.current) {
        setModalDetailedInfo((currentInfo) => ({
          ...currentInfo,
          experiencias: professionalExperience.data,
        }));
      }
    } catch (error) {
      console.error('Error loading employee detail', error);
    } finally {
      if (requestId === detailRequestId.current) {
        setDetailLoading(false);
      }
    }
  };

  const handleClose = () => {
    detailRequestId.current += 1;
    setModalDetailedInfo({});
    setOpen(false);
    setDetailLoading(false);
  };

  const handlePhoto = (id: string, fotoSrc: string) => {
    const detailedInfo = results.find((item: ObjectType) => item.id === id);
    detailedInfo.photo = fotoSrc;
  };

  const handleChangeStatus = (id: string, newStatus: string, newItem: StatusChangeObject) => {
    setStatusChangeLoading(true);
    const detailedInfo = results.find((item: ObjectType) => item.id === id);
    const oldStatus = detailedInfo.estado;
    worksWorkersAPI.updateStatus(id, newItem)
      .then(() => {
        const newHistory = [newItem, ...detailedInfo.historialCambios];
        detailedInfo.estado = newStatus;
        detailedInfo.historialCambios = newHistory;
        setModalDetailedInfo((prevHistory) => ({
          ...prevHistory,
          estado: newStatus,
          historialCambios: newHistory,
        }));
        sendStatusChangeEventAnalytics(id, newStatus, oldStatus);
        setStatusChangeLoading(false);
      }).catch(() => {
        console.error('Error updating status');
        setStatusChangeLoading(false);
      });
  };

  useEffect(() => {
    if (isFilterUpdated) {
      scrollToRef();
      dispatch(toggleIsFilterUpdated(listType));
    }
  }, [lists, isFilterUpdated]);

  const renderGrid = () => (
    <Grid
      item
      xs={12}
      ref={scrollToTop}
      style={
        {
          minHeight: '84vh',
          maxHeight: '84vh',
        }
      }
    >
      <DetailedInfoModal
        open={open}
        listType={listType}
        modalDetailedInfo={modalDetailedInfo}
        detailLoading={detailLoading}
        handleClose={handleClose}
        handleChangeStatus={handleChangeStatus}
      />
      <CivilWorksWorkerCards
        results={results}
        lastItemList={lastItemList}
        listType={listType}
        handleOpen={handleOpen}
        handlePhoto={handlePhoto}
      />
      {isLoading && <LinearProgress color="inherit" />}
    </Grid>
  );

  const renderComponent = () => {
    if (statusChangeLoading) {
      return (
        <>
          <LoadingComponent opacity={0} />
          {renderGrid()}
        </>
      );
    }
    return renderGrid();
  };

  return (
    <Grid
      item
      p={1}
      xs={6}
      style={{ height: '100%' }}
    >
      <InfiniteScrollListFilter
        totalResults={totalResults}
        listType={listType}
      />
      <Grid
        container
        justifyContent="center"
        className={classes.infiniteScroll}
      >
        {
          isError
            ? <p>Error</p>
            : renderComponent()
        }
      </Grid>
    </Grid>
  );
};

export default memo(InfiniteScrollList);
